import { useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { useUiStore } from '../../../shared/hooks'
import type { NavItem, NavSection } from '../Shell.types'

/** Whether a link is the current page (as NavLink decides, plus sub-pages). */
const isCurrent = (item: NavItem, path: string) =>
  item.end
    ? path === item.to
    : path === item.to || path.startsWith(`${item.to}/`)

/** Accent-insensitive, case-insensitive text match for the menu filter. */
const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

/**
 * Open/closed state of the menu sections (remembered per browser), the
 * section of the current page, and the menu filter: while filtering, only
 * matching links show and their sections open.
 */
export function useNavSections(sections: NavSection[]) {
  const { pathname } = useLocation()
  const closed = useUiStore((s) => s.navClosedGroups)
  const setOpen = useUiStore((s) => s.setNavGroupsOpen)
  const [filter, setFilter] = useState('')

  const shown = useMemo(() => {
    const q = fold(filter.trim())

    if (!q) return sections

    return sections
      .map((s) => ({
        ...s,
        items: fold(s.title).includes(q)
          ? s.items
          : s.items.filter((i) => fold(i.label).includes(q)),
      }))
      .filter((s) => s.items.length)
  }, [sections, filter])

  const activeId = sections.find((s) =>
    s.items.some((i) => isCurrent(i, pathname)),
  )?.id

  const filtering = filter.trim() !== ''
  const ids = sections.map((s) => s.id)
  const allOpen = ids.every((id) => !closed.includes(id))

  return {
    sections: shown,
    filter,
    setFilter,
    filtering,
    activeId,
    isOpen: (id: string) => filtering || !closed.includes(id),
    toggle: (id: string) => setOpen([id], closed.includes(id)),
    allOpen,
    setAll: (open: boolean) => setOpen(ids, open),
  }
}
